import { IBtiDataSchema, IOfferSchema } from '@cian/api-models/offer-card/v1/get-offer-data';
import { OutsideClick } from '@cian/outside-click-component';
import { numberToPrettyString, mergeStyles } from '@cian/utils';
import * as React from 'react';

import { getCianPriceRange, toFixedPrettyDecimal, getCalculatorUrl } from './helpers';
import { IconQuestion } from './icons';
import * as tracking from './tracking';
import * as styles from './CostEstimate.css';

export interface ICostEstimateProps {
  bti: IBtiDataSchema | null;
  offer: IOfferSchema;

  openBtiDataErrorPopup(): void;
}

export interface ICostEstimateState {
  isPriceRangePopupOpen: boolean;
}

const NBSP = '\u00A0';

export class CostEstimate extends React.Component<ICostEstimateProps, ICostEstimateState> {
  public state: ICostEstimateState = {
    isPriceRangePopupOpen: false,
  };

  public render() {
    const { bti, offer } = this.props;

    if (!bti) {
      return null;
    }

    const { districtData } = bti;

    const priceRange = this.renderPriceRange();

    const averageHousePriceSqm =
      (bti.houseData &&
        bti.houseData.priceSqm &&
        renderAverage({
          price: bti.houseData.priceSqm,
          description: 'Средняя цена за м\u00B2',
          dynamic: bti.houseData.priceSqmDiff,
        })) ||
      null;

    const averageHouseRoomRentPrice =
      (bti.houseData &&
        bti.houseData.roomRentPrice &&
        offer.roomsCount &&
        renderAverage({
          price: bti.houseData.roomRentPrice,
          pricePostfix: '\u20BD/мес',
          description: `Ставка аренды ${offer.roomsCount > 4 ? 'многокомн.\u00a0кв.' : `${offer.roomsCount}-комн.кв.`}`,
          dynamic: bti.houseData.roomRentPriceDiff,
        })) ||
      null;

    const averageDistrictPriceSqm =
      (bti.districtData &&
        bti.districtData.priceSqm &&
        renderAverage({
          price: bti.districtData.priceSqm,
          description: 'Средняя цена за м\u00B2',
          dynamic: bti.districtData.priceSqmDiff,
        })) ||
      null;

    const averageDistrictRoomSalePrice =
      (bti.districtData &&
        bti.districtData.roomSalePrice &&
        offer.roomsCount &&
        renderAverage({
          price: bti.districtData.roomSalePrice,
          description: `Средняя цена ${offer.roomsCount > 4 ? 'многокомн.\u00a0кв.' : `${offer.roomsCount}-комн.кв.`}`,
          dynamic: bti.districtData.roomSalePriceDiff,
        })) ||
      null;

    const averageDistrictRoomRentPrice =
      (bti.districtData &&
        bti.districtData.roomRentPrice &&
        offer.roomsCount &&
        renderAverage({
          price: bti.districtData.roomRentPrice,
          pricePostfix: '\u20BD/мес',
          description: `Ставка аренды ${offer.roomsCount > 4 ? 'многокомн.\u00a0кв.' : `${offer.roomsCount}-комн.кв.`}`,
          dynamic: bti.districtData.roomRentPriceDiff,
        })) ||
      null;

    const isStatisticalDataAvailable =
      !!averageHousePriceSqm ||
      !!averageHouseRoomRentPrice ||
      !!averageDistrictPriceSqm ||
      !!averageDistrictRoomSalePrice ||
      !!averageDistrictRoomRentPrice;

    if (!priceRange && !isStatisticalDataAvailable) {
      return null;
    }

    const realtyCalcUrl = getCalculatorUrl(offer, bti);

    return (
      <div className={styles['container']} id="valuation">
        <div className={styles['wrapper']}>
          <div className={styles['title']}>
            <span>Оценка стоимости</span>
            <span className={styles['note']}>Аналитика ЦИАН</span>
          </div>
          {/* tslint:disable-next-line: max-line-length */}
          <p className={styles['description']}>
            Оценка квартиры рассчитывается по ценам в объявлениях в похожих домах в этом районе.
            <br />
            Базовая стоимость определяется для сделки прямой продажи в стандартные сроки.
          </p>
          {this.renderPriceRange()}
        </div>
        {isStatisticalDataAvailable && (
          <div className={styles['averages']}>
            {!!averageHousePriceSqm && !!averageHouseRoomRentPrice && (
              <div className={styles['wrapper']}>
                <div className={styles['title']}>В этом доме</div>
                <div>
                  {averageHousePriceSqm}
                  {averageHouseRoomRentPrice}
                </div>
              </div>
            )}
            <div className={styles['wrapper']}>
              <div className={styles['title']}>
                В районе{(districtData && districtData.districtTitle && ` ${districtData.districtTitle}`) || ''}
              </div>
              <div>
                {averageDistrictPriceSqm}
                {averageDistrictRoomSalePrice}
                {averageDistrictRoomRentPrice}
              </div>
            </div>
          </div>
        )}
        <div className={styles['wrapper']}>
          {!!realtyCalcUrl && (
            <p className={styles['footnote']}>Узнать подробнее про оценку продажи и аренды квартир</p>
          )}
          <div className={styles['footer']}>
            {!!realtyCalcUrl && (
              <a
                className={styles['button']}
                href={realtyCalcUrl}
                target="_blank"
                onClick={() => {
                  tracking.trackEstimateClick('estimate_button');
                }}
              >
                Оценка квартир
              </a>
            )}
            <button className={styles['link']} onClick={this.handleErrorClick}>
              Ошибка в данных о доме или районе?
            </button>
          </div>
        </div>
      </div>
    );
  }

  private handleErrorClick = () => {
    tracking.trackErrorClick();
    this.props.openBtiDataErrorPopup();
  };

  private renderPriceRange() {
    const { offer, bti } = this.props;

    if (!bti || !bti.houseData) {
      return null;
    }

    const cianPriceRange = getCianPriceRange(bti.houseData);
    const realtyCalcUrl = getCalculatorUrl(offer, bti);

    if (!cianPriceRange || !realtyCalcUrl) {
      return null;
    }

    return (
      <>
        <div className={styles['price_range-title']}>
          Диапазон цены
          <span
            className={styles['price_range-info']}
            onClick={() => {
              this.setState({ isPriceRangePopupOpen: true });
            }}
          >
            <IconQuestion />
            {this.state.isPriceRangePopupOpen && (
              <OutsideClick onOutsideClick={() => this.setState({ isPriceRangePopupOpen: false })}>
                <div className={styles['price_range-tooltip']}>
                  Оценка квартиры рассчитывается по ценам
                  <br />
                  в объявлениях в похожих домах в этом районе. Базовая
                  <br />
                  стоимость определяется для сделки прямой продажи
                  <br />
                  в стандартные сроки
                  <br />
                  <br />
                  По данным ЦИАН в 2016-2018 гг. реальная цена сделки
                  <br />
                  в среднем на 8% ниже цены объявления
                  <br />
                  <br />
                  <a
                    href={realtyCalcUrl}
                    target="_blank"
                    onClick={() => {
                      tracking.trackEstimateClick('more');
                    }}
                  >
                    Подробнее
                  </a>
                </div>
              </OutsideClick>
            )}
          </span>
        </div>
        <a
          className={styles['price_range-link']}
          href={realtyCalcUrl}
          target="_blank"
          onClick={() => {
            tracking.trackEstimateClick('price');
          }}
        >
          {cianPriceRange}
        </a>
      </>
    );
  }
}

interface IRenderAverageParameters {
  price: number;
  pricePostfix?: string;
  description: string;
  dynamic?: number;
}

function renderAverage({ price, pricePostfix, description, dynamic }: IRenderAverageParameters) {
  return (
    <div className={styles['average']}>
      <div className={styles['average-price']}>
        {`${numberToPrettyString(Math.round(price))} ${pricePostfix ? pricePostfix : '\u20BD'}`.replace(/ /g, NBSP)}
      </div>
      <div className={styles['average-description']}>{description}</div>
      {!!dynamic && renderAverageDynamics(dynamic)}
    </div>
  );
}

function renderAverageDynamics(dynamic: number) {
  return (
    <div className={styles['average-dynamics']}>
      <span {...mergeStyles(styles['average-dynamic'], dynamic < 0 ? styles['average-dynamic--negative'] : null)}>
        {`${dynamic > 0 ? '+' : ''} ${toFixedPrettyDecimal(dynamic)} % `}
      </span>
      за 12 месяцев
    </div>
  );
}
